import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type ApiError, apiFetchJson } from '../../lib/apiFetch.js';

/**
 * TanStack Query hooks for the ServiceTitan connection (integration P1).
 *
 * ⚠ CREDENTIALS ONLY EVER TRAVEL ONE WAY. The client POSTs a client id, client secret and app key
 * and never reads any of them back — the server stores them encrypted and returns only the last
 * four of the client id, which is enough to tell WHICH key pair is installed. There is deliberately
 * no query that returns a secret, so no cache here can hold one.
 *
 * The tenant is resolved server-side from the auth context; the SPA never sends a tenant id
 * (threat T-22-tenant). Everything rides `apiFetch`, which bakes in `X-Tenant-Subdomain` and the
 * double-submit CSRF token on non-safe methods.
 */

/** ServiceTitan's two worlds. Keys from one never work against the other. */
export type ServiceTitanEnvironment = 'integration' | 'production';

/** What the server is willing to say about an installed credential. Never the credential. */
export interface ServiceTitanConnection {
  environment: ServiceTitanEnvironment;
  stTenantId: string;
  clientIdLast4: string;
  updatedAt: string | null;
}

export interface ServiceTitanConnectionsResponse {
  ok: boolean;
  connections: ServiceTitanConnection[];
  /**
   * False when the Worker has no encryption key. The card explains that saving is unavailable for
   * a reason the admin cannot fix themselves, rather than letting them fill the form and fail on
   * submit with nothing actionable.
   */
  encryptionAvailable: boolean;
}

export interface ServiceTitanCredentialInput {
  environment: ServiceTitanEnvironment;
  stTenantId: string;
  clientId: string;
  clientSecret: string;
  appKey: string;
}

export interface ServiceTitanTestResult {
  ok: boolean;
  businessUnitCount: number;
}

export function serviceTitanQueryKey(): readonly unknown[] {
  return ['servicetitan', 'connections'] as const;
}

export function useServiceTitanConnectionsQuery(enabled = true) {
  return useQuery<ServiceTitanConnectionsResponse>({
    queryKey: serviceTitanQueryKey(),
    queryFn: () =>
      apiFetchJson<ServiceTitanConnectionsResponse>('/api/v1/servicetitan/credentials'),
    enabled,
  });
}

export function useSaveServiceTitanCredentialsMutation() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean }, Error, ServiceTitanCredentialInput>({
    mutationFn: (input) =>
      apiFetchJson<{ ok: boolean }>('/api/v1/servicetitan/credentials', {
        method: 'PUT',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: serviceTitanQueryKey() });
    },
  });
}

export function useDeleteServiceTitanCredentialsMutation() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean }, Error, ServiceTitanEnvironment>({
    mutationFn: (environment) =>
      apiFetchJson<{ ok: boolean }>(`/api/v1/servicetitan/credentials/${environment}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: serviceTitanQueryKey() });
    },
  });
}

/**
 * Prove the stored credentials actually reach ServiceTitan.
 *
 * The server reads business units rather than only minting a token, because a token proves the
 * id/secret pair alone — not the app key, the scope, or the ServiceTitan tenant id. The failure is
 * surfaced as an `ApiError` whose message is the server's stable code, so the card can say which
 * of those four things to go and fix.
 */
export function useTestServiceTitanConnectionMutation() {
  return useMutation<ServiceTitanTestResult, ApiError, ServiceTitanEnvironment>({
    mutationFn: (environment) =>
      apiFetchJson<ServiceTitanTestResult>('/api/v1/servicetitan/test-connection', {
        method: 'POST',
        body: JSON.stringify({ environment }),
        headers: { 'Content-Type': 'application/json' },
      }),
  });
}
